import {Component, inject, OnInit, signal, DestroyRef} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {DataService} from '../../../../services/store/data.service';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {Capability} from '../../../../classes/models/employee/Capability';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {EmployeeCapabilityService} from '../../../../services/http/employee/employee-capability.service';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

export interface CapabilityCreateEmptyData {
  employeePositionId?: EmployeePositionId;
}

/**
 * This component deletes capabilities for selected week by actually creating a new capability with empty capability types.
 */
@Component({
  selector: 'app-capability-create-empty',
  templateUrl: './capability-create-empty.component.html',
  styleUrls: ['./capability-create-empty.component.less'],
  imports: [SHARED_IMPORTS]
})
export class CapabilityCreateEmptyComponent implements OnInit {
  dataService = inject(DataService);
  /** Identifiers of an employee and position. */
  readonly employeePositionId = signal<EmployeePositionId>(undefined);
  /** `True` if request sending to backend is in progress. */
  loading = false;
  /** The first date from which a capability is allowed to be deleted. */
  editDate: string;
  /** The Monday of the week, from which the new empty capabilities should be created. */
  startDate: string;
  private employeeCapabilityService = inject(EmployeeCapabilityService);
  private toastr = inject(ToastrService);
  private ruleCheckerService = inject(RuleCheckerService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }

  static open(modalService: NgbModal, data: CapabilityCreateEmptyData): NgbModalRef {
    const modalRef = modalService.open(CapabilityCreateEmptyComponent);
    const instance = modalRef.componentInstance as CapabilityCreateEmptyComponent;
    instance.employeePositionId.set(data.employeePositionId);
    return modalRef;
  }

  /**
   * Sets up an edit date on page initialization.
   */
  ngOnInit() {
    this.editDate = this.dataService.editDate;
    this.startDate = this.editDate;
  }

  /**
   * Sends request to create new empty capabilities.
   * @param modalComponent a modal component.
   */
  confirm(modalComponent: ModalComponent): void {
    const capability = new Capability([], this.startDate);
    this.employeeCapabilityService.addCapability(this.employeePositionId(), capability)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading = false;
                this.toastr.success('Capabilities successfully deleted for the selected week');
                this.ruleCheckerService.check();
              },
              error: error => {
                this.loading = false;
                this.toastr.error(error.error.message);
              }
            });
  }
}